import React, { useRef } from "react";
import styled from "styled-components";
import Dropdown from 'react-bootstrap/Dropdown';
import classNames from "classnames";
import { useDropdownContext } from "./DropdownContext";
import chevronDown from "./chevron-down.svg";
import chevronDownWhite from "./chevron-down-white.svg";
import { nanoid } from "nanoid";
import { AutomationTestIdElementType, AutomationTestIdFeature, createDataTestId } from "../../../utilities/automation";

interface IDropdownToggleProps {
    id?: string;
    title: string;
    name: string;
    required?: boolean;
    className?: string;
    inputClassName?: string;
    isWhite?: boolean;
    feature?: AutomationTestIdFeature;
}

const DropdownToggle = styled((props: IDropdownToggleProps) => {
    const { selectedItem } = useDropdownContext();
    const inputIdRef = useRef(props.id || nanoid());
    const feature = props.feature || AutomationTestIdFeature.FORM;
    return (
        <Dropdown.Toggle variant="light" aria-label={props.title} className={classNames(props.className, props.inputClassName, "w-100 p-0", {'text-white border-white': props.isWhite})}  bsPrefix="custom-dropdown-toggle" tabIndex={0}
            data-test-id={createDataTestId({feature, element: AutomationTestIdElementType.DROPDOWN, specifier: 'trigger'})}>
            {/* This select is used for evaluation the required property and show a native error, but the react-bootstrap dropdown is shown on top of it */}
            <select id={inputIdRef.current} tabIndex={-1} className={classNames("dropdown-value border-0 w-100", {'selected-value': (selectedItem?.eventKey || selectedItem?.text)})} name={props.name} required={props.required}
                data-test-id={createDataTestId({ feature, element: AutomationTestIdElementType.INPUT, specifier: "select" })}>
                <option value={`${selectedItem?.eventKey || selectedItem?.text || ''}`} dangerouslySetInnerHTML={{__html: `${selectedItem?.html ?? (selectedItem?.text || '')}`}} />
            </select>
            <label htmlFor={inputIdRef.current} className={classNames("dropdown-title", {'text-white': props.isWhite})}>{props.title} {props.required ? <sup>*</sup> : null}</label>
        </Dropdown.Toggle>
    );
})`

&.custom-dropdown-toggle {
    position: relative;
    border: 0 none;
    border-color: #000 !important;
    min-height: 2.56rem;
    background-color: transparent;
    border-radius: 0;
    border-bottom: 1px solid;

    &:hover, &:focus {
        color: var(--black, #2E2E2B);
    }
    &:active {
        background-color: transparent !important;
    }

    &.border-white {
        border-color: #FFF !important; // This one is needed  as we are overriding bootstrap styles
    }

}
&.custom-dropdown-toggle:before {
    position: absolute;
    content: "";
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 3;
}
&.custom-dropdown-toggle::after {
    display: inline-block;
    content: "";
    position: absolute;
    right: 8px;
    width: 15px;
    height: 8px;
    top: 50%;
    margin-top: 3px;
    background: url(${props => (!!props.isWhite) ? chevronDownWhite : chevronDown}) no-repeat;
}

.dropdown-title {
    position: absolute;
    z-index: 0;
    color: var(--mud, #5E514D);
    font-weight: 500;
    line-height: normal;
    transition: all .2s ease-in-out;
    top: 14px;
    left: 0;
    opacity: 0.5;
    font-size: 0.88889rem;
    text-transform: none;
    &.text-white {
        opacity: 1;
    }
}

.dropdown-value {
    color: inherit;
    background-color: transparent;
    font-size: 0.88889rem;
    font-weight: 500;
    line-height: 1rem;
    cursor: pointer;
    z-index: 1;
    padding: 26.5px 24px 9px 0;
    appearance: none;
    -webkit-appearance: none;
}

.dropdown-value.selected-value + .dropdown-title {
    top: 5px;
    opacity: 1;
    font-size: 0.55556rem;
    color: var(--mud, #5E514D);
}
`;

export default DropdownToggle;